/**
 * Bulk-create — CSV / xlsx parsing + template generation.
 *
 * Framework-agnostic: takes an `ArrayBuffer` (from `File.arrayBuffer()`) and
 * returns `ParsedOrder[]`. The React layer owns the file input and state; this
 * module only does pure parsing + a workbook download side-effect.
 */
import { read, utils } from "xlsx";
import type { ParsedOrder } from "./types";
import { parseDimensionUnit, parseWeightUnit } from "./units";

/** Spreadsheet column headers, in template order. */
export const TEMPLATE_HEADERS = [
  "Recipient Name",
  "Recipient Phone",
  "Recipient Email",
  "Recipient Address",
  "Recipient Unit",
  "Recipient City",
  "Recipient Province",
  "Recipient Postal Code",
  "Package Weight",
  "Weight Unit",
  "Package Length",
  "Package Width",
  "Package Height",
  "Dimension Unit",
  "Delivery Instructions",
] as const;

/** Example row used in the downloadable template. */
const TEMPLATE_EXAMPLE_ROW = [
  "John Smith",
  "4165551234",
  "john@example.com",
  "123 Main Street",
  "Unit 4B",
  "Toronto",
  "ON",
  "M5V 2K4",
  "2", // Package Weight
  "kg", // Weight Unit (kg, lb, oz, g)
  "30", // Package Length
  "20", // Package Width
  "15", // Package Height
  "cm", // Dimension Unit (cm, in)
  "Leave at door",
];

/** Format a phone number to Canadian E.164 (+1XXXXXXXXXX) when possible. */
export const formatCanadianPhone = (phone: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  // 10 digits → assume Canadian, add +1
  if (digits.length === 10) return `+1${digits}`;
  // 11 digits starting with 1 → add +
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  // Already prefixed → keep only digits after +
  if (phone.startsWith("+")) return `+${digits}`;
  // Unclear format → return original
  return phone;
};

/** Stable row id factory — reused by parsing, manual entry, and v1 prefill. */
export const makeOrderId = (index: number): string =>
  `order-${index}-${Date.now()}`;

/**
 * Look up a column value with flexible header matching: exact match first, then
 * a case-insensitive alphanumeric partial match against any provided alias.
 */
export const getColumnValue = (
  row: Record<string, string>,
  ...possibleHeaders: string[]
): string => {
  for (const header of possibleHeaders) {
    if (row[header] !== undefined) return String(row[header]);
    const key = Object.keys(row).find((k) =>
      k.toLowerCase().includes(header.toLowerCase().replace(/[^a-z]/g, ""))
    );
    if (key && row[key] !== undefined) return String(row[key]);
  }
  return "";
};

/** Map one raw spreadsheet row to a `ParsedOrder`. */
export const rowToParsedOrder = (
  row: Record<string, string>,
  index: number
): ParsedOrder => ({
  id: makeOrderId(index),
  recipientName: getColumnValue(row, "Recipient Name"),
  recipientPhone: formatCanadianPhone(getColumnValue(row, "Recipient Phone")),
  recipientEmail: getColumnValue(row, "Recipient Email"),
  recipientAddress: getColumnValue(row, "Recipient Address"),
  recipientUnit: getColumnValue(row, "Recipient Unit", "Unit", "Apt", "Suite"),
  recipientCity: getColumnValue(row, "Recipient City", "City"),
  recipientProvince: getColumnValue(
    row,
    "Recipient Province",
    "Province",
    "State"
  ),
  recipientPostalCode: getColumnValue(
    row,
    "Recipient Postal Code",
    "Postal Code",
    "Zip"
  ),
  weight: Number(getColumnValue(row, "Package Weight", "Weight")) || 1,
  weightUnit: parseWeightUnit(getColumnValue(row, "Weight Unit")),
  length: Number(getColumnValue(row, "Package Length", "Length")) || 30,
  width: Number(getColumnValue(row, "Package Width", "Width")) || 20,
  height: Number(getColumnValue(row, "Package Height", "Height")) || 15,
  dimensionUnit: parseDimensionUnit(getColumnValue(row, "Dimension Unit")),
  deliveryNotes: getColumnValue(row, "Delivery Instructions", "Delivery Notes"),
  status: "pending",
  selected: true,
});

/**
 * Parse a CSV / xlsx file buffer into `ParsedOrder[]`. Rows missing both a
 * recipient name and address are dropped. Throws on an unreadable workbook —
 * the caller surfaces the toast.
 */
export const parseSpreadsheet = (data: ArrayBuffer): ParsedOrder[] => {
  const workbook = read(data);
  const worksheet = workbook.Sheets[workbook.SheetNames[0]];
  const jsonData = utils.sheet_to_json<Record<string, string>>(worksheet);

  return jsonData
    .filter(
      (row) =>
        getColumnValue(row, "Recipient Name") ||
        getColumnValue(row, "Recipient Address")
    )
    .map(rowToParsedOrder);
};

/** Build the template workbook (headers + one example row). */
export const buildTemplateWorkbook = () => {
  const wb = utils.book_new();
  const ws = utils.aoa_to_sheet([[...TEMPLATE_HEADERS], TEMPLATE_EXAMPLE_ROW]);
  utils.book_append_sheet(wb, ws, "Orders");
  return wb;
};

/** Trigger a browser download of the bulk-ship template. */
export const downloadTemplate = async (
  fileName = "bulk-ship-template.xlsx"
): Promise<void> => {
  const wb = buildTemplateWorkbook();
  const { writeFileXLSX } = await import("xlsx");
  writeFileXLSX(wb, fileName);
};
